import React from "react";
import "./MoneyAdvice.css";
import { Helmet } from "react-helmet-async";

const title = "Money Advice (Managing Your Money)";
const pageTitle = "Money Advice | Respected Solutions";
const metaDescription =
  "Practical money advice from Respected Solutions: budgeting, saving, borrowing, investing, and resources to help Nigerians manage finances smarter.";

const MoneyAdvice = () => {
  return (
    <section className="money-advice-container">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
      </Helmet>

      <div
        className="money-advice-hero-slide"
        style={{ backgroundImage: `url(/assets/images/rs/money_advice.png)` }}
      >
        <div className="money-advice-hero-overlay">
          <div className="money-advice-hero-content">
            <h1>{title}</h1>
            <p>Practical tips for smarter financial decisions.</p>
          </div>
        </div>
      </div>

      <div className="money-advice-inner">
        <div className="money-advice-inner-content">
          <section className="money-advice-intro">
            <p>
              At Respected Solutions &amp; Investment Limited, we know that
              managing your money can sometimes feel overwhelming. From
              day-to-day expenses to bigger financial commitments, staying in
              control of your finances is not always easy. This page offers
              practical tips and useful contacts to help you make smarter
              decisions about budgeting, saving, borrowing, and investing.
            </p>
            <p className="disclaimer">
              Please note: this guidance is for general information only and
              does not replace professional financial advice.
            </p>
          </section>

          <section className="money-advice-budgeting">
            <h2>Budgeting and Saving</h2>
            <p>
              Creating a budget is one of the simplest ways to stay in control
              of your money. Split your income into essentials, savings, and
              discretionary spending.
            </p>
            <ul>
              <li>
                <strong>Budgeting Tools:</strong> Mobile apps like Kuda, Opay,
                or Carbon can help track spending, while traditional banks such
                as GTBank and UBA also offer budgeting features.
              </li>
              <li>
                <strong>Saving First:</strong> Try to set aside savings as soon
                as you receive your income. Platforms like PiggyVest and
                Cowrywise, or savings accounts with Stanbic IBTC and Zenith
                Bank, make saving easier and more rewarding.
              </li>
            </ul>
          </section>

          <section className="money-advice-emergency">
            <h2>Emergency Funds</h2>
            <p>
              Unexpected bills are part of everyday life. Medical costs, school
              fees, or urgent travel can quickly strain your budget. An
              emergency fund provides a financial cushion when you need it most.
            </p>
            <p>
              Consider keeping this money in a separate bank account or digital
              wallet such as Opay or Paga, so it remains accessible but not
              mixed with everyday spending.
            </p>
          </section>

          <section className="money-advice-borrowing">
            <h2>Borrowing Money</h2>
            <p>
              There are times when borrowing is unavoidable. What matters most
              is borrowing responsibly and from trusted sources.
            </p>
            <p>
              If you are finding your finances particularly difficult to manage,
              the following organizations may be able to help. This list is not
              exhaustive but should give you a good starting point:
            </p>
            <ul>
              <li>
                ▪️ <strong>Government &amp; Cooperative Schemes - </strong>
                Options include the Bank of Industry (BOI), the Development Bank
                of Nigeria (DBN), and local cooperatives (ajo or esusu) which
                support members with soft loans.
              </li>
              <li>
                ▪️ <strong>Commercial Banks - </strong> Salary advances and
                personal loans from GTBank, Access Bank, FirstBank, and Zenith
                Bank.
              </li>
              <li>
                ▪️ <strong>Microfinance Banks - </strong> Loans for individuals
                and businesses from LAPO MFB, ACCION MFB, and AB Microfinance
                Bank.
              </li>
              <li>
                ▪️ <strong>Fintech Loan Apps - </strong> Quick access to funds
                via Carbon, FairMoney, Branch, and Okash (note: higher interest
                rates may apply).
              </li>
              <li>
                ▪️ <strong>Collateral Loans - </strong> At Respected Solutions,
                we offer safe and secure loans against valuable items such as
                cars, luxury watches, jewelry, and gadgets. This provides a fair
                alternative to unregulated lenders.
              </li>
            </ul>
            <a className="money-advice-cta-btn" href="/contact-us">
              Learn More About Our Loan Services
            </a>
          </section>

          <section className="money-advice-investments">
            <h2>Investments</h2>
            <p>
              While saving keeps money safe, investing helps it grow. Options in
              Nigeria include:
            </p>
            <ul>
              <li>
                ▪️ Treasury Bills and FG Bonds – Government-backed, accessible
                via banks.
              </li>
              <li>
                ▪️ Mutual Funds – Offered by firms like ARM and Stanbic IBTC.
              </li>
              <li>
                ▪️ Digital Platforms – Services such as Cowrywise and Risevest.
              </li>
              <li>
                ▪️ Respected Solutions Investment Plans – Flexible plans
                designed to give competitive monthly returns, tailored to both
                short- and long-term goals.
              </li>
            </ul>
            <a
              className="money-advice-cta-btn"
              href="/partnership-collaboration"
            >
              Explore Investment Plans
            </a>
          </section>

          <section className="money-advice-literacy">
            <h2>Staying Informed</h2>
            <p>
              Financial literacy is essential. Keep up to date with market
              trends, interest rates, and currency movements to make better
              decisions.
            </p>
            <p>
              For Nigerians dealing in foreign exchange, RespectedFx provides
              reliable daily USD, GBP, and EUR rates, as well as safe transfers
              and cash delivery services.
            </p>
          </section>

          <section className="money-advice-support">
            <h2>Need Support?</h2>
            <p>
              At Respected Solutions, we are committed to helping Nigerians
              achieve financial stability. Whether you need a loan, an
              investment plan, or a safe place to keep your valuables through
              our Pawn &amp; Park service, we’re here to provide secure and
              reliable solutions.
            </p>
            <p>
              📞 You can also contact us directly for more personalised money
              advice, guidance, or enquiries. Our team is always available to
              support you in making the right financial decisions.
            </p>
            <a href="/contact-us" className="money-advice-cta-btn">
              Contact Us
            </a>
          </section>

          <section className="money-advice-resources">
            <h2>Quick Links &amp; Resources</h2>
            <ul>
              <li>💳 Banks: GTBank, Access Bank, Zenith Bank, FirstBank</li>
              <li>
                🏦 Microfinance: LAPO MFB, ACCION MFB, AB Microfinance Bank
              </li>
              <li>📱 Loan Apps: Carbon, FairMoney, Branch, Okash</li>
              <li>
                🏛 Government Support: Development Bank of Nigeria (DBN), Bank of
                Industry (BOI)
              </li>
              <li>👥 Community Savings: Ajo and Esusu groups</li>
              <li>
                🔐 Respected Solutions: Loans, investments, and Pawn &amp; Park
                secure storage
              </li>
            </ul>
          </section>

          <section className="money-advice-closing">
            <p>
              💡 Respected Solutions – your trusted partner for loans,
              investments, and smarter money decisions.
            </p>
          </section>
        </div>
      </div>
    </section>
  );
};

export default MoneyAdvice;
